<script setup>
import {
    onMounted,
    ref,
    defineProps,
    reactive,
    watch,
    computed,
    onUnmounted,
    nextTick,
} from "vue";
import { jsPDF } from "jspdf";
import { io } from "socket.io-client";
import Konva from "konva";
import {
    simplify,
    loadImageAsset,
    getId,
    pathsToPoints,
    hexToRgbA,
    defenseTools,
    offenseTools,
    isValidTool,
    ICONS,
} from "./whiteboard_util";
import {
    CNTextbox,
    CNTextboxFactory,
    Circle,
    CircleOutline,
    Rect,
    RectOutline,
} from "./cn_player";
import { CNLineFactory } from "./cn_line";
import { CNLineAcross } from "./cn_misc";
import { CNText } from "./cn_text";
import { CNPenFactory } from "./cn_pen";
import simplifyJs from "simplify-js";
import emitter from "tiny-emitter/instance";
import basketPlayer from "./basketPlayer.json";
import useWhiteboard from "./useWhiteboard";
import { sleep } from "../../utils/timer";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import Animation from "./cn_anim";
import ThreeCameraView from "./cn_three_view";

const RADIUS_MAP = [15, 25, 40];
const LINE_SIZE_MAP = [2, 4, 6];

const heightRatio = 960 / 1920;
const width = 1920;
const height = width * heightRatio;

Konva._fixTextRendering = true;

const props = defineProps({
    data: {
        type: Object,
    },
    play: {
        type: Object,
    },
    sport: {
        type: String,
    },
    whiteboardApi: {
        type: String,
    },
    username: {
        type: String,
    },
    title: {
        type: String,
    },
    teamId: {
        type: Number,
    },
    notes: {
        type: String,
    },
    isPlay: {
        type: Boolean,
    },
    isGuest: {
        type: Boolean,
        default: false,
    },
    viewOnly: {
        type: Boolean,
        default: false,
    },
    relativeToContainer: {
        type: Boolean,
        default: false,
    },
    isFree: {
        type: Boolean,
        default: false,
    },
    websocketUrl: {
        type: String,
        default: "ws://localhost/ws",
    },
    env: {
        type: String,
        default: "local",
    },
});

const {
    currentTool,
    currentColor,
    isConfirmClear,
    currentSize,
    isNoteOpen,
    isLocked,
    preview,
    animation,
} = useWhiteboard();

function hydratePlayAnimation(animationJson) {
    let saved = null;
    if (animationJson && typeof animationJson === "object") {
        saved = animationJson;
    } else if (typeof animationJson === "string" && animationJson.trim()) {
        try {
            saved = JSON.parse(animationJson);
        } catch (error) {
            console.error("Unable to load saved play animation", error);
        }
    }

    animation.value.active = false;
    animation.value.currentFrame = 1;
    animation.value.frames = saved?.frames
        ? JSON.parse(JSON.stringify(saved.frames))
        : {};
    animation.value.playerSpeeds = saved?.playerSpeeds
        ? JSON.parse(JSON.stringify(saved.playerSpeeds))
        : {};
    animation.value.playing = false;
    animation.value.selectedPlayerId = null;
    animation.value.selectedPlayerLabel = "";
    animation.value.selectedPlayerSpeed = 1;
    animation.value.hasBall = false;
    animation.value.camera3d = false;
}

if (props.isPlay) {
    hydratePlayAnimation(props.play?.animation_json);
}

import WhiteboardAiDrawer from "./WhiteboardAiDrawer.vue";

const isAiOpen = ref(false);

function handleAiActions(actions) {
    if (actions && actions.length > 0) {
        actions.forEach((act) => {
            handleEvents(act, true);
        });
    }
}

const state = reactive({
    isToolbarCollapsed: false,
    isFlipped: false,

    isEditMode: false,

    lastDrawId: 0,
    isDrawing: false,

    drawBuffers: [],
    undoBuffers: [],
    linePoints: [],

    isLoadVideoModalOpen: false,
    activePlay: props.play,

    selectedShape: null,
    selectedShapes: [],
    isGroupSelected: false,
    isGroupLocked: false,
    clipboards: [],
    lastCopiedShape: null,
    lastCopyCount: 0,
    selectAreaActive: false,

    groupDrawId: null,

    notes: props.play ? props.play.play_note : props.notes,

    mdata: {
        x: 0,
        y: 0,
    },
    bgOpacity: 30,
    mresult: "",

    groups: {},
});

function generateM(x, y) {
    x = parseInt(x);
    y = parseInt(y);
    state.mresult = `
M${x},${y}
L${x + 2},${y + 2}
L${y + 2},${x + 2}
L${y},${x}
Z
`;
    console.log({ a: state.mresult });
}

watch(
    () => state.bgOpacity,
    (val) => {
        bgImg.opacity(val / 100);
    }
);

/* watch(
    () => state.mdata.x,
    (val) => {
        generateM(state.mdata.x, state.mdata.y);
    }
);

watch(
    () => state.mdata.y,
    (val) => {
        generateM(state.mdata.x, state.mdata.y);
    }
); */

watch(
    () => currentTool.value,
    (val) => {
        state.selectedShape = null;
        toggleDragger(val === "mouse");
        toggleRemover(val === "eraser");
        if (val !== "selectArea") {
            tr.nodes([]);
            state.selectedShapes = [];
            state.isGroupSelected = false;
        }
    }
);

watch(
    () => currentColor.value,
    (val) => handleSelectColor(val)
);

watch(
    () => state.selectedShapes,
    (val) => {
        if (val.length > 1) {
            state.groupDrawId = ++state.lastDrawId;
        } else {
            state.groupDrawId = null;
        }
    }
);

let stage;
let layer;
let animLayer;
let bgLayer;
let lineLayer;
let trLayer;
let bgImg;
let tr;
let trResizable;
let dragBox;
let selectionRectangle = null;
let CNAnim;
let threeCameraView;
let lineLayerListeningBeforeAnimation = true;
let lineLayerOpacityBeforeAnimation = 1;

onMounted(() => {
    stage = new Konva.Stage({
        container: "container",
        width: width,
        height: height,
    });

    bgLayer = new Konva.Layer();
    stage.add(bgLayer);

    lineLayer = new Konva.Layer();
    stage.add(lineLayer);

    layer = new Konva.Layer();
    stage.add(layer);

    animLayer = new Konva.Layer();
    stage.add(animLayer);

    trLayer = new Konva.Layer();
    stage.add(trLayer);

    tr = new Konva.Transformer({
        rotateEnabled: false,
        resizeEnabled: false,
        name: "selectionTranformer",
    });
    trResizable = new Konva.Transformer({
        flipEnabled: false,
        rotateEnabled: false,
        name: "resizeTransformer",
        resizeEnabled: true,
        shouldOverdrawWholeArea: true,
    });

    trLayer.add(tr);
    trLayer.add(trResizable);

    dragBox = new Konva.Rect({
        strokeWidth: 0,
        fill: "rgba(255,0,0,0.2)", // fully transparent
        draggable: true,
        listening: true, // can capture pointer events
    });

    layer.add(dragBox);
    dragBox.moveToTop();

    tr.on("dragmove transform", () => {
        layer.batchDraw();
    });

    window.addEventListener("resize", updateScale);
    if (props.relativeToContainer) {
        setTimeout(() => {
            updateScale();
        }, 1000);
    } else {
        updateScale();
    }

    addWhiteBackground();
    if (props.data?.background) {
        addBackgroundImage(props.data.background);
    } else if (props.play?.background) {
        addBackgroundImage(props.play.background);
    }

    // make stage container focusable
    stage.container().tabIndex = 1;
    // focus it
    // also stage will be in focus on its click
    stage.container().focus();

    stage.container().addEventListener("keydown", (e) => {
        if (e.key === "Backspace" || e.key === "Delete") {
            if (state.selectedShape) {
                state.selectedShape?.removeAction();
                state.selectedShape = null;
            }
        }
    });

    addWatermark();

    if (!props.isPlay) {
        signaling_socket = io(props.whiteboardApi, {
            path: "/ws-api",
            extraHeaders: {
                "whiteboard-id": props.data.room_code,
            },
            transports: ["websocket"], // keep both for fallback
            withCredentials: true,
        }); // Connect even if we are in a subdir behind a reverse proxy

        signaling_socket.on("connect", function () {
            console.log("Websocket connected!");
            signaling_socket.on("drawToWhiteboard", function (content) {
                handleEvents(content);
            });

            signaling_socket.on("updateNotes", function (content) {
                state.notes = content.notes;
            });

            signaling_socket.emit("joinWhiteboard", {
                wid: props.data.room_code,
                at: "",
                windowWidthHeight: {
                    w: $(window).width(),
                    h: $(window).height(),
                },
            });
        });
    }
    loadImageAssets()
        .then(() => {
            getWhiteboard();
            generatePlayThumbnail();

            let currentLine;

            let circle = null;
            let rect = null;
            let startPos = null;
            let lineId = null;
            let allLinePoints = [];

            function handleStageClick(e) {
                if (
                    stage.getIntersection(stage.getPointerPosition())?.attrs
                        ?.name !== "board"
                ) {
                    const curIns = stage.getIntersection(
                        stage.getPointerPosition()
                    );

                    const parent = getParentShape(curIns);
                    const shapeInstance = parent?.attrs.instance;
                    handleStartResize(parent, curIns);

                    console.log({ shapeInstance });
                    if (!shapeInstance?.group) {
                        tr.nodes([]);
                        state.isGroupSelected = false;
                        state.selectedShapes = [];
                        trLayer.batchDraw();
                    }

                    isLocked.value = shapeInstance?.isLocked || false;
                    if (!shapeInstance?.isLocked) {
                        return; // prevent drawing if something is being dragged
                    }
                } else {
                    if (currentTool.value === "mouse") {
                        tr.nodes([]);
                        resetResizeables();
                        state.selectedShapes = [];
                        state.selectedShape = null;
                        state.isGroupSelected = false;
                        trLayer.batchDraw();
                    }
                }

                const pos = getScaledPointerPosition(stage);
                if (isPlayerTool(currentTool.value)) {
                    const radius = RADIUS_MAP[currentSize.value];
                    handleEvents(
                        {
                            t: "addTextBox",
                            d: [
                                currentColor.value,
                                currentColor.value,
                                currentSize.value,
                                pos.x - radius,
                                pos.y - radius,
                                "tx" + +new Date(),
                                currentTool.value,
                                false,
                            ],
                            drawId: ++state.lastDrawId,
                        },
                        true
                    );
                } else if (currentTool.value === "text") {
                    if (state.isEditMode) return;
                    const content = {
                        t: "text",
                        d: [
                            "tx" + +new Date(),
                            pos.x,
                            pos.y,
                            "Sample Text",
                            currentColor.value,
                            currentSize.value,
                        ],
                        drawId: ++state.lastDrawId,
                    };
                    const inst = drawText(content, true);
                    state.drawBuffers.push(content);

                    if (inst?.edit) {
                        inst.edit();
                    }
                    state.isEditMode = true;
                } else if (currentTool.value === "lineAcross") {
                    handleEvents(
                        {
                            t: "lineAcross",
                            c: currentColor.value,
                            th: currentSize.value,
                            d: ["tx" + +new Date(), pos.y],
                            drawId: ++state.lastDrawId,
                        },
                        true
                    );
                }
            }

            stage.on("click", handleStageClick);
            stage.on("tap", handleStageClick);

            stage.on("mousedown touchstart", (e) => {
                /* console.log({
                    s: stage.getIntersection(stage.getPointerPosition())
                }) */
                if (
                    stage.getIntersection(stage.getPointerPosition())?.attrs
                        ?.name !== "board"
                ) {
                    const curIns = stage.getIntersection(
                        stage.getPointerPosition()
                    );
                    const parent = getParentShape(curIns);
                    const shapeInstance = parent?.attrs.instance;

                    isLocked.value = shapeInstance?.isLocked || false;
                    if (!shapeInstance?.isLocked) {
                        return; // prevent drawing if something is being dragged
                    }
                }

                const pos = getScaledPointerPosition(stage);
                startPos = pos;

                if (currentTool.value === "selectArea") {
                    if (!pos) {
                        return;
                    }
                    if (!selectionRectangle) {
                        selectionRectangle = new Konva.Rect({
                            name: "selectionRectangle",
                            fill: "rgba(0,0,255,0.1)",
                            stroke: "blue",
                            visible: true,
                        });
                        trLayer.add(selectionRectangle);
                    } else {
                        selectionRectangle.visible(true);
                    }
                    selectionRectangle.width(0);
                    selectionRectangle.height(0);
                    state.selectAreaActive = true;
                    return;
                }
                if (isDrawingTool(currentTool.value)) {
                    state.isDrawing = true;

                    const options = {
                        stroke: currentColor.value,
                        strokeWidth: LINE_SIZE_MAP[currentSize.value],
                        points: [pos.x, pos.y],
                        lineCap: "round",
                        lineJoin: "round",
                        draggable: true,
                        name: currentTool.value,
                    };

                    if (isDrawingToolDotted(currentTool.value)) {
                        options.dash = [10, 10];
                    }

                    if (currentTool.value == "multilineHammer") {
                        options.pointerWidth = 20;
                        options.pointerLength = 0;
                        currentLine = new Konva.Arrow(options);
                    } else {
                        currentLine = new Konva.Line(options);
                    }

                    if (isFreeDrawingTool(currentTool.value)) {
                        lineId = ++state.lastDrawId;
                        currentLine.id(
                            currentTool.value + "" + lineId + "temp"
                        );
                        state.linePoints = [pos.x, pos.y];
                    }

                    lineLayer.add(currentLine);
                } else if (
                    ["circle", "circleOutline"].includes(currentTool.value)
                ) {
                    state.isDrawing = true;
                    circle = new Konva.Circle({
                        x: pos.x,
                        y: pos.y,
                        radius: 1,
                        stroke: "rgba(0,0,0,0.5)",
                        strokeWidth: 2,
                        dash: [4, 4],
                        name: currentTool.value,
                        id: "tx" + +new Date(),
                        draggable: true,
                    });
                    assignShapeDraggable(circle);
                    layer.add(circle);
                } else if (
                    ["rect", "rectOutline"].includes(currentTool.value)
                ) {
                    state.isDrawing = true;
                    rect = new Konva.Rect({
                        x: startPos.x,
                        y: startPos.y,
                        width: 1,
                        height: 1,
                        stroke: "rgba(0,0,0,0.5)",
                        strokeWidth: 2,
                        dash: [4, 2],
                        name: currentTool.value,
                        id: "tx" + +new Date(),
                        draggable: true,
                    });
                    assignShapeDraggable(rect);
                    layer.add(rect);
                }
            });

            stage.on("mousemove touchmove", (e) => {
                if (state.selectAreaActive) {
                    const pos2 = getScaledPointerPosition(stage);
                    if (!pos2) return;
                    selectionRectangle.setAttrs({
                        x: Math.min(startPos.x, pos2.x),
                        y: Math.min(startPos.y, pos2.y),
                        width: Math.abs(pos2.x - startPos.x),
                        height: Math.abs(pos2.y - startPos.y),
                    });
                    trLayer.batchDraw();

                    return;
                }
                if (!state.isDrawing) return;

                const pos = getScaledPointerPosition(stage);
                if (isDrawingTool(currentTool.value)) {
                    const newPoints = currentLine
                        .points()
                        .concat([pos.x, pos.y]);
                    currentLine.points(newPoints);

                    if (isFreeDrawingTool(currentTool.value)) {
                        allLinePoints.push(pos.x, pos.y);
                        state.linePoints.push(pos.x, pos.y);
                        if (state.linePoints.length >= 8) {
                            handleEvents(
                                {
                                    t: currentTool.value,
                                    d: state.linePoints,
                                    c: currentColor.value,
                                    th: currentSize.value,
                                    drawId: lineId,
                                    v: 2,
                                    visible: false,
                                },
                                true
                            );
                            state.linePoints = [];
                        }
                    }
                } else if (
                    ["circle", "circleOutline"].includes(currentTool.value)
                ) {
                    const dx = pos.x - startPos.x;
                    const dy = pos.y - startPos.y;
                    const radius = Math.sqrt(dx * dx + dy * dy);

                    circle.radius(radius);

                    layer.batchDraw();
                } else if (
                    ["rect", "rectOutline"].includes(currentTool.value)
                ) {
                    const newX = Math.min(pos.x, startPos.x);
                    const newY = Math.min(pos.y, startPos.y);
                    const newWidth = Math.abs(pos.x - startPos.x);
                    const newHeight = Math.abs(pos.y - startPos.y);

                    rect.position({ x: newX, y: newY });
                    rect.size({ width: newWidth, height: newHeight });

                    layer.batchDraw();
                }
            });

            stage.on("mouseup touchend", () => {
                if (state.selectAreaActive) {
                    state.selectAreaActive = false;
                    selectionRectangle.visible(false);
                    const box = selectionRectangle.getClientRect();
                    const selected = getSelectableShapes(box, [
                        layer,
                        lineLayer,
                    ]);

                    if (selected.length > 0) {
                        state.selectedShapes = selected;
                        tr.nodes(selected);
                        trLayer.batchDraw();
                        layer.batchDraw();
                    } else {
                        state.selectedShapes = [];
                        tr.nodes([]);
                        trLayer.batchDraw();
                        layer.batchDraw();
                    }
                    return;
                }
                if (!state.isDrawing) {
                    return;
                }
                if (isFreeDrawingTool(currentTool.value)) {
                    // state.linePoints.push(pos.x, pos.y);
                    currentLine.destroy();
                    currentLine.stroke("red");
                    lineLayer.batchDraw();
                    handleEvents(
                        {
                            t: currentTool.value,
                            d: state.linePoints,
                            c: currentColor.value,
                            th: currentSize.value,
                            drawId: lineId,
                            v: 2,
                            visible: true,
                        },
                        true
                    );
                } else if (
                    [
                        "bezierLine",
                        "bezierLineDashed",
                        "bezierLineTab",
                        "bezierLineTabDashed",
                    ].includes(currentTool.value)
                ) {
                    const newPoints = simplifyJs(
                        pathsToPoints(currentLine.points()),
                        20,
                        true
                    );
                    const points = createBezierPoints(newPoints);
                    handleEvents(
                        {
                            t: currentTool.value,
                            d: [
                                points[0].x,
                                points[0].y,
                                points[3].x,
                                points[3].y,
                                points[1].x,
                                points[1].y,
                                points[2].x,
                                points[2].y,
                                "tx" + +new Date(),
                            ],
                            c: currentColor.value,
                            th: currentSize.value,
                            drawId: ++state.lastDrawId,
                        },
                        true
                    );

                    currentLine.destroy();
                } else if (isMultilineTool(currentTool.value)) {
                    const newPoints = simplifyJs(
                        pathsToPoints(currentLine.points()),
                        20,
                        true
                    );
                    currentLine.destroy();
                    lineLayer.batchDraw();
                    handleEvents(
                        {
                            t: currentTool.value,
                            d: newPoints.map((p) => ({ x: p.x, y: p.y })),
                            c: currentColor.value,
                            th: currentSize.value,
                            drawId: ++state.lastDrawId,
                        },
                        true
                    );
                } else if (
                    ["circle", "circleOutline"].includes(currentTool.value)
                ) {
                    const scale = stage.scale();
                    let _currentColor = currentColor.value;
                    if (_currentColor.length >= 9) {
                        _currentColor = _currentColor.slice(0, 7);
                    }
                    const color = hexToRgbA(_currentColor);

                    circle.dash([]);
                    circle.opacity(0.2);
                    if (currentTool.value === "circleOutline") {
                        circle.stroke(color);
                        circle.strokeWidth(3 * currentSize.value);
                        const content = CircleOutline.toJSON(
                            circle.attrs,
                            scale
                        );
                        handleEvents(
                            {
                                ...content,
                                drawId: ++state.lastDrawId,
                            },
                            true
                        );
                    } else {
                        circle.stroke(currentColor.value);
                        circle.strokeWidth(3 * currentSize.value);
                        circle.fill(color);
                        const content = Circle.toJSON(circle.attrs, scale);
                        handleEvents(
                            {
                                ...content,
                                drawId: ++state.lastDrawId,
                            },
                            true
                        );
                    }
                    circle.destroy();
                    layer.batchDraw();
                } else if (
                    ["rect", "rectOutline"].includes(currentTool.value)
                ) {
                    let _currentColor = currentColor.value;
                    if (_currentColor.length >= 9) {
                        _currentColor = _currentColor.slice(0, 7);
                    }
                    const color = hexToRgbA(_currentColor);
                    rect.dash([]);
                    rect.opacity(0.2);

                    if (currentTool.value === "rectOutline") {
                        rect.stroke(color);
                        rect.strokeWidth(2 * currentSize.value);
                        const content = RectOutline.toJSON(rect.attrs);
                        handleEvents(
                            {
                                ...content,
                                drawId: ++state.lastDrawId,
                            },
                            true
                        );
                    } else {
                        rect.stroke(null);
                        rect.fill(color);
                        const content = Rect.toJSON(rect.attrs);
                        handleEvents(
                            {
                                ...content,
                                drawId: ++state.lastDrawId,
                            },
                            true
                        );
                    }
                    rect.destroy();
                    layer.batchDraw();
                }

                lineId = null;
                state.linePoints = [];
                allLinePoints = [];

                state.isDrawing = false;
            });
        })
        .catch((e) => {
            console.error(e);
        });

    document.addEventListener("keydown", handleKeyboardEvent);
});
onUnmounted(() => {
    threeCameraView?.destroy();
    threeCameraView = null;
    CNAnim?.destroy();
    animation.value.active = false;
    animation.value.playing = false;
    document.removeEventListener("keydown", handleKeyboardEvent);
});

async function generatePlayThumbnail() {
    if (props.isPlay) {
        if (props.play?.play_id && !props.play?.thumbnail) {
            const image = await stage.toBlob({
                pixelRatio: 2, // double resolution
            });

            const formData = new FormData();
            formData.append("play_thumbnail", image);

            axios
                .post(
                    `/plays/save-thumbnail?play_id=${props.play.play_id}`,
                    formData
                )
                .then((res) => {
                    console.log("saving good image");
                })
                .catch((e) => {
                    console.error(e);
                });
        }
    }
}

function getParentShape(ins) {
    if (ins?.parent) {
        return ins.parent;
    }
    return ins;
}

function getSelectablePathPoints(shape) {
    const instance = shape.attrs.instance;
    let points = [];

    if (instance?.name?.startsWith("bezierLine")) {
        const anchors = [
            instance.start,
            instance.control1,
            instance.control2,
            instance.end,
        ];
        if (anchors.some((anchor) => !anchor)) return [];

        const [start, control1, control2, end] = anchors.map((anchor) => ({
            x: anchor.x(),
            y: anchor.y(),
        }));
        points = Array.from({ length: 33 }, (_, index) => {
            const t = index / 32;
            const inverse = 1 - t;
            return {
                x:
                    inverse ** 3 * start.x +
                    3 * inverse ** 2 * t * control1.x +
                    3 * inverse * t ** 2 * control2.x +
                    t ** 3 * end.x,
                y:
                    inverse ** 3 * start.y +
                    3 * inverse ** 2 * t * control1.y +
                    3 * inverse * t ** 2 * control2.y +
                    t ** 3 * end.y,
            };
        });
    } else if (typeof instance?.getPoints === "function") {
        const flatPoints = instance.getPoints();
        for (let index = 0; index < flatPoints.length; index += 2) {
            points.push({ x: flatPoints[index], y: flatPoints[index + 1] });
        }
    } else if (typeof instance?.toRelativePoints === "function") {
        points = instance.toRelativePoints();
    }

    const transform = shape.getAbsoluteTransform();
    return points.map((point) => transform.point(point));
}

function segmentIntersectsRect(start, end, rect) {
    let min = 0;
    let max = 1;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const p = [-dx, dx, -dy, dy];
    const q = [
        start.x - rect.left,
        rect.right - start.x,
        start.y - rect.top,
        rect.bottom - start.y,
    ];

    for (let index = 0; index < p.length; index++) {
        if (p[index] === 0) {
            if (q[index] < 0) return false;
            continue;
        }

        const ratio = q[index] / p[index];
        if (p[index] < 0) min = Math.max(min, ratio);
        else max = Math.min(max, ratio);
        if (min > max) return false;
    }

    return true;
}

function pathIntersectsSelectionBox(shape, box) {
    const points = getSelectablePathPoints(shape);
    if (!points.length) return null;

    const hitbox = shape.findOne(".hitbox");
    const padding = (hitbox?.strokeWidth?.() || 0) / 2;
    const rect = {
        left: box.x - padding,
        right: box.x + box.width + padding,
        top: box.y - padding,
        bottom: box.y + box.height + padding,
    };

    if (
        points.some(
            (point) =>
                point.x >= rect.left &&
                point.x <= rect.right &&
                point.y >= rect.top &&
                point.y <= rect.bottom
        )
    ) {
        return true;
    }

    for (let index = 1; index < points.length; index++) {
        if (segmentIntersectsRect(points[index - 1], points[index], rect)) {
            return true;
        }
    }

    return false;
}

function getSelectableShapes(box, layers) {
    let selected = [];

    layers.map((layer) => {
        const shapes = layer.getChildren();
        const sel = shapes.filter((shape) => {
            const pathIntersection = pathIntersectsSelectionBox(shape, box);
            return (
                (pathIntersection ??
                    Konva.Util.haveIntersection(box, shape.getClientRect())) &&
                shape.attrs.name !== "selectionRectangle" &&
                shape.attrs.name !== "selectionTranformer" &&
                shape.attrs.name !== "lineAcross" &&
                !shape.attrs.instance?.isLocked
            );
        });
        selected = [...selected, ...sel];
    });

    return selected;
}

function toggleGroup() {
    const nodes = tr.nodes();
    const groupId = "tx" + +new Date();
    nodes.forEach((node) => {
        node.attrs.instance.group = groupId;
    });
    state.groups = {
        ...state.groups,
        [groupId]: nodes,
    };
    state.isGroupSelected = true;
}

function toggleUngroup() {
    if (!state.isGroupSelected) {
        return;
    }
    const nodes = tr.nodes();
    const groupId = nodes[0].attrs.instance.group;
    nodes.forEach((node) => {
        node.attrs.instance.group = null;
        node.attrs.instance.isLocked = false;
    });
    state.groups = Object.keys(state.groups).filter((g) => g !== groupId);
    tr.nodes([]);
    trLayer.batchDraw();
    layer.batchDraw();
    state.isGroupSelected = false;
}

function toggleGroupLock() {
    tr.shouldOverdrawWholeArea(false);
    const nodes = tr.nodes();
    nodes.forEach((node) => {
        node.attrs.instance.isLocked = true;
        node.draggable(false);
    });
    state.isGroupLocked = true;
}

function toggleGroupUnlock() {
    tr.shouldOverdrawWholeArea(true);
    const nodes = tr.nodes();
    nodes.forEach((node) => {
        node.attrs.instance.isLocked = false;
        node.draggable(true);
    });
    state.isGroupLocked = false;
}

function selectGroup(groupId) {
    state.selectedShape = null;
    state.selectedShapes = state.groups[groupId];
    state.isGroupSelected = true;
    const shapes = state.groups[groupId];
    tr.nodes(shapes);
    if (shapes[0]?.attrs?.instance?.isLocked) {
        state.isGroupLocked = true;
        tr.shouldOverdrawWholeArea(false);
    } else {
        tr.shouldOverdrawWholeArea(true);
    }
    trLayer.batchDraw();
    layer.batchDraw();
}

function toggleDragger(state) {
    const nodes = lineLayer.find(".dragger, .anchor, .linepath");
    nodes.forEach((node) => {
        node.visible(state);
        if (node.name() == "dragger" && node.strokeWidth) {
            node.strokeWidth(0);
        }

        if (node.name() == "anchor") {
            node.visible(false);
            node.strokeWidth(0);
        }

        if (node.name() === "linepath") {
            node.visible(false);
            node.strokeWidth(0);
        }
    });
}

function hideAllDragger() {
    const nodes = lineLayer.find(".dragger, .anchor, .linepath");
    nodes.forEach((node) => {
        if (node.name() == "dragger" && node.strokeWidth) {
            node.strokeWidth(0);
        }

        if (node.name() == "anchor" || node.name() == "linepath") {
            node.visible(false);
        }
    });
    hidePlayerHitboxes();
}

function hidePlayerHitboxes() {
    stage.find(".player-hitbox").forEach((hitbox) => {
        hitbox.stroke("rgba(14, 140, 233, 0)");
    });
}

function toggleRemover(state) {
    var nodes = layer.find(".remover");
    nodes.forEach((node) => {
        node.visible(state);
    });

    nodes = lineLayer.find(".remover");
    nodes.forEach((node) => {
        node.visible(state);
    });
}

let signaling_socket;

function updateNotes(notes) {
    state.notes = notes;
    signaling_socket.emit("updateNotes", { notes });
}

emitter.on("wb-change-background", function (val) {
    if (val === "none") {
        clearBackgroundImage(true);
    } else {
        changeBackgroundImage(
            {
                d: val,
            },
            true
        );
    }
});

emitter.on("wb-video-tagged", function (play) {
    handleEvents({ t: "changePlay", d: [play.play_id] }, true);
});

emitter.on("wb-load-play", function (play) {
    try {
        layer.destroyChildren();
        lineLayer.destroyChildren();
        state.drawBuffers = [];
        state.undoBuffers = [];
        state.lastDrawId = 0;
        layer.draw();
        lineLayer.draw();
        sendFunction({ t: "clear" });
        sendFunction({ t: "changePlay", d: [play.play_id] });

        state.activePlay = play;
        state.taggedVideosCount = play.videos?.length;
        state.notes = play.play_note;

        const json = cleanPlayJson(play.play_json);
        /* for (let i = 0; i < json.length; i++) {
            if (json[i].drawId == 0) {
                json[i].drawId = i + 1;
            }
        } */
        const flatten = simplify(json, props.sport);
        for (let i = 0; i < flatten.length; i++) {
            // console.log(flatten[i]);
            // flatten[i].drawId = i;
            flatten[i].b = 1;
            handleEvents(flatten[i], true);
        }
        fixFlip();
        state.lastDrawId = 1;
    } catch (e) {
        console.error(e);
    }
});

emitter.on("wb-load-template", function (data) {
    try {
        if (data?.playbook.playbook_title === "Offense") {
            clearOffenses(true);
        }
        if (data?.playbook.playbook_title === "Defense") {
            clearDefenses(true);
        }
        const val = data.play_json;
        const json = cleanPlayJson(val);
        const flatten = simplify(json, props.sport);
        for (let i = 0; i < flatten.length; i++) {
            flatten[i].drawId = i;
            flatten[i].b = 1;
            handleEvents(flatten[i], true);
        }
        fixFlip();
    } catch (e) {
        console.error(e);
    }
});

emitter.on("getBufferData", async function () {
    bgLayer.visible(false);
    toggleDragger(false);
    toggleRemover(false);
    const image = await stage.toBlob({
        pixelRatio: 2, // double resolution
    });
    const prevTool = currentTool.value;
    currentTool.value = "";
    const data = JSON.parse(JSON.stringify(state.drawBuffers));
    emitter.emit("getBufferDataReply", {
        image,
        data,
        notes: state.notes,
        animation: getPersistedAnimation(),
    });
    setTimeout(function () {
        currentTool.value = prevTool;
    }, 200);
    bgLayer.visible(true);
});

emitter.on("getBufferDataFull", async function (mode) {
    let image;
    if (layer.children.length === 0 && lineLayer.children.length === 0) {
        emitter.emit("getBufferDataReplyFull", {
            image: null,
        });
        return;
    }
    if (mode === "blob") {
        image = await stage.toBlob({
            pixelRatio: 2, // double resolution
        });
    } else {
        image = await stage.toDataURL({
            pixelRatio: 2, // double resolution
        });
    }

    emitter.emit("getBufferDataReplyFull", {
        image,
    });
});

function handleEvents(content, isNew) {
    if (isValidTool(content.t) && content.drawId != null) {
        state.lastDrawId = content.drawId;
    }
    switch (content.t) {
        case "setTextboxPosition":
            setTextboxPosition(content, isNew);
            break;
        case "move":
            move(content, isNew);
            break;
        case "remove":
            remove(content, isNew);
            break;
        case "addTextBox":
            addTextbox(content, isNew);
            break;
        case "resizeCircle":
            resizeCircle(content, isNew);
            break;
        case "resizeRect":
            resizeRect(content, isNew);
            break;
        case "removeTextbox":
            removeTextbox(content, isNew);
            break;
        case "clear":
            clear(isNew);
            break;
        case "undo":
            undo(isNew);
            break;
        case "redo":
            redo(isNew);
            break;
        case "clearCanvas":
            clearCanvas(isNew);
            break;
        case "clearDefenses":
            clearDefenses(isNew);
            break;
        case "clearOffenses":
            clearOffenses(isNew);
            break;
        case "setTextboxFontSize":
            currentSize.value = content.d[1];
            break;
        case "shadeTextbox":
            shadeTextbox(content, isNew);
            break;
        case "bezierLine":
        case "bezierLineDashed":
        case "bezierLineTab":
        case "bezierLineTabDashed":
            drawBezierLine(content, isNew);
            break;
        case "editBezierLine":
            editBezierLine(content, isNew);
            break;
        case "editMultiline":
            editMultiline(content, isNew);
            break;
        case "multiline":
        case "multilineDotted":
        case "multilineArrow":
        case "multilineHammer":
        case "multilineDashedArrow":
        case "penSquigglyArrow":
        case "penSquigglyTab":
        case "penSquigglyFirstArrow":
        case "penSquigglyFirstTab":
            drawMultiline(content, isNew);
            break;
        case "lineAcross":
            drawLineAcross(content, isNew);
            break;
        case "pen":
        case "penDotted":
        case "penTab":
        case "penArrow":
        case "penDottedArrow":
            drawPen(content, isNew);
            break;
        case "changeOffense":
            changeOffense(content, isNew);
            break;
        case "changeDefense":
            changeDefense(content, isNew);
            break;
        case "changeBackground":
            changeBackgroundImage(content, isNew);
            break;
        case "changeIcon":
            changeIcon(content, isNew);
            break;
        case "changePlay":
            changePlay(content, isNew);
            break;
        case "setColor":
            setColor(content, isNew);
            break;
        case "setShadedColor":
            setShadedColor(content, isNew);
            break;
        case "setBackgroundColor":
            // setColor(content, isNew);
            break;
        case "text":
            drawText(content, isNew);
            break;
        case "setText":
            setText(content, isNew);
            break;
    }
    fixFlip();

    if (isValidTool(content.t)) {
        state.drawBuffers.push(content);
    }
}

function drawText(tool, isNew) {
    tool.removerImage = removerImage;
    const inst = new CNText(tool);
    const shape = inst.element;

    inst.setClickOutsideAction(function (evt) {
        state.isEditMode = false;
    });

    inst.setDblClickAction(function (evt) {
        state.isEditMode = true;
        inst.edit();
    });

    inst.setDragStart(function (evt) {
        hideAllDragger(false);
    });

    inst.setClickAction(function (evt) {
        selectShape(inst);
    });

    inst.setUpdateAction(function (text) {
        const action = {
            t: "setText",
            d: [inst.id, text],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });

    inst.setDragEnd(function (evt) {
        const pos = inst.element.position();
        const action = {
            t: "move",
            d: [inst.id, pos.x, pos.y],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });

    inst.setRemoverAction(function (evt) {
        inst.element.destroy();
        layer.draw();
        const action = {
            t: "remove",
            d: [inst.id],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });

    layer.add(shape);

    delete tool.removerImage;

    if (isNew) {
        sendFunction({
            ...tool,
            drawId: tool.drawId != null ? tool.drawId : ++state.lastDrawId,
        });
    }

    return inst;
}

function setText(tool, isNew) {
    const shape = layer.findOne("#" + tool.d[0]);
    const inst = shape.attrs.instance;
    inst.updateText(tool.d[1]);
    layer.draw();
}

function resizeCircle(content, isNew) {
    let shape = layer.findOne(`#${getId(content)}`);
    const [id, x, y, rx, ry] = content.d;

    shape.attrs.instance.resizeMove(x, y, rx, ry);

    layer.batchDraw();

    if (isNew) {
        sendFunction({
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        });
    }
}

function resizeRect(content, isNew) {
    let shape = layer.findOne(`#${getId(content)}`);
    const [id, x, y, width, height] = content.d;

    shape.attrs.instance.resizeMove(x, y, width, height);

    layer.batchDraw();

    if (isNew) {
        sendFunction({
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        });
    }
}

function resetResizeables() {
    const cur = trResizable.nodes();
    cur.forEach((node) => {
        const realElement = node.attrs.realElement;
        realElement.visible(true);
        node.destroy();
    });
    trResizable.nodes([]);

    trLayer.batchDraw();
}

function handleStartResize(shape, element) {
    resetResizeables();

    const resizableElements = [
        "rect",
        "rectOutline",
        "circle",
        "circleOutline",
    ];
    if (shape?.attrs.name && resizableElements.includes(shape.attrs.name)) {
        const tempShape = element.clone({
            x: shape.x(),
            y: shape.y(),
            draggable: true,
            id: "temp" + shape.id(),
            attrs: {
                realElement: shape,
            },
        });

        shape.visible(false);
        trLayer.add(tempShape);
        trResizable.nodes([tempShape]);
        trResizable.getLayer().batchDraw();

        tempShape.on("transformend dragend", () => {
            const scaleX = tempShape.scaleX();
            const scaleY = tempShape.scaleY();

            const pos = tempShape.position();
            let x = pos.x;
            let y = pos.y;

            let width = element.width() * Math.abs(scaleX);
            let height = element.height() * Math.abs(scaleY);

            let action;

            if (["rect", "rectOutline"].includes(shape.attrs.name)) {
                action = {
                    t: "resizeRect",
                    d: [shape.attrs.id, x, y, width, height],
                    drawId: ++state.lastDrawId,
                };
            } else if (["circle", "circleOutline"].includes(shape.attrs.name)) {
                action = {
                    t: "resizeCircle",
                    d: [
                        shape.attrs.id,
                        x,
                        y,
                        tempShape.radiusX() * scaleX,
                        tempShape.radiusY() * scaleY,
                    ],
                    drawId: ++state.lastDrawId,
                };
            }

            handleEvents(action, true);
            state.drawBuffers.push(action);
            layer.batchDraw();
        });

        if (["rect", "rectOutline"].includes(shape.attrs.name)) {
            trResizable.enabledAnchors([
                "top-left",
                "top-center",
                "top-right",
                "middle-right",
                "middle-left",
                "bottom-left",
                "bottom-center",
                "bottom-right",
            ]);
        } else if (["circle", "circleOutline"].includes(shape.attrs.name)) {
            trResizable.enabledAnchors([
                "top-left",
                "top-center",
                "top-right",
                "middle-right",
                "middle-left",
                "bottom-left",
                "bottom-center",
                "bottom-right",
            ]);
        }
    } else {
        resetResizeables();
    }
}

function assignShapeDraggable(shape) {
    shape.on("dragstart", function () {
        hideAllDragger();
    });
    shape.on("dragend", function (evt) {
        const pos = shape.position();
        let action = {};
        const drawId =
            state.groupDrawId != null ? state.groupDrawId : ++state.lastDrawId;
        if (["circle", "circleOutline"].includes(shape.attrs.name)) {
            const r = shape.attrs.radius;
            const x = pos.x - r;
            const y = pos.y - r;

            action = {
                t: "setTextboxPosition",
                d: [shape.attrs.id, y, x],
                drawId,
            };
        } else if (isPlayerTool(shape.attrs.name)) {
            action = {
                t: "setTextboxPosition",
                d: [shape.attrs.id, pos.y - 25, pos.x - 25],
                drawId,
            };
        } else {
            action = {
                t: "setTextboxPosition",
                d: [shape.attrs.id, pos.y, pos.x],
                drawId,
            };
        }
        state.drawBuffers.push(action);
        sendFunction(action);
    });
}

function selectShape(inst) {
    state.selectedShape = inst;
    const color = inst.getSelectedColor?.() ?? inst.color;
    if (typeof color === "string") {
        currentColor.value = color;
    }
}

function showSelectedPlayerHitbox(inst) {
    hideAllDragger();
    inst.hitbox?.stroke("#0e8ce9");
}

function addTextbox(tool, isNew) {
    tool.removerImage = removerImage;
    tool.basketImage = basketImage;

    const inst = CNTextboxFactory(tool);

    if (!inst) {
        return;
    }

    const shape = inst.element;

    inst.setRemoverAction(function (evt) {
        shape.destroy();
        layer.draw();
        const action = {
            t: "removeTextbox",
            d: [shape.attrs.id],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });

    if (inst.name.startsWith("icon:")) {
        inst.setClickAction(function (evt) {
            showSelectedPlayerHitbox(inst);
            const nextIndex = (inst.iconIndex + 1) % ICONS.length;
            const action = {
                t: "changeIcon",
                d: [inst.id, nextIndex, inst.iconIndex],
                drawId: ++state.lastDrawId,
            };
            inst.updateIcon(nextIndex);
            state.drawBuffers.push(action);
            sendFunction(action);
            selectShape(inst);
        });
    } else if (inst.name === "ShadedCross") {
        // do nothing
    } else {
        if (inst.setClickAction) {
            inst.setClickAction(function (evt) {
                showSelectedPlayerHitbox(inst);
                if (inst.group) {
                    selectGroup(inst.group);
                    return;
                }
                selectShape(inst);
            });
        }
    }

    if (inst.name.startsWith("offense:basket")) {
        inst.setEditorAction(function (text) {
            const action = {
                t: "changeOffense",
                d: [inst.id, 0, 0, text],
                drawId: ++state.lastDrawId,
            };
            state.drawBuffers.push(action);
            sendFunction(action);
        });
    } else if (inst.name.startsWith("offense:")) {
        inst.setEditorAction(function (text) {
            const action = {
                t: "changeOffense",
                d: [inst.id, 0, 0, text],
                drawId: ++state.lastDrawId,
            };
            state.drawBuffers.push(action);
            sendFunction(action);
        });
    } else if (inst.name.startsWith("defense:x")) {
        inst.setEditorAction(function (text) {
            const action = {
                t: "changeDefense",
                d: [inst.id, 0, 0, text],
                drawId: ++state.lastDrawId,
            };
            state.drawBuffers.push(action);
            sendFunction(action);
        });
    } else if (inst.name.startsWith("defense:")) {
        inst.setEditorAction(function (text) {
            const action = {
                t: "changeDefense",
                d: [inst.id, 0, 0, text],
                drawId: ++state.lastDrawId,
            };
            state.drawBuffers.push(action);
            sendFunction(action);
        });
    } else if (inst.name === "ShadedCross") {
        inst.setChangeColorRequestAction(function (posIndex, color, nextShade) {
            const wasSelected =
                state.selectedShape?.id === inst.id &&
                inst.selectedCircleIndex === posIndex;
            inst.selectedCircleIndex = posIndex;
            showSelectedPlayerHitbox(inst);
            selectShape(inst);

            if (wasSelected) {
                handleEvents(
                    {
                        t: "shadeTextbox",
                        d: [inst.id, nextShade, posIndex],
                        drawId: ++state.lastDrawId,
                    },
                    true
                );
            }
        });
    }

    assignShapeDraggable(shape);
    layer.add(shape);

    delete tool.removerImage;
    delete tool.basketImage;

    if (isNew) {
        const action = {
            ...tool,
            drawId: tool.drawId != null ? tool.drawId : ++state.lastDrawId,
        };
        // state.drawBuffers.push(action);
        sendFunction(action);
    }
}

function setTextboxPosition(content, isNew) {
    let shape = layer.findOne(`#${getId(content)}`);
    if (shape) {
        if (["circle", "circleOutline"].includes(shape.attrs.name)) {
            shape.position({
                x: content.d[2] + shape.attrs.radius,
                y: content.d[1] + shape.attrs.radius,
            });
        } else if (isPlayerTool(shape.attrs.name)) {
            shape.position({
                x: content.d[2] + 25,
                y: content.d[1] + 25,
            });
        } else {
            shape.position({ x: content.d[2], y: content.d[1] });
        }
    }

    if (isNew) {
        const action = {
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        };
        sendFunction(action);
    }
}

function shadeTextbox(content, isNew) {
    let shape = layer.findOne(`#${getId(content)}`);
    if (shape) {
        shape.attrs.instance.shadeTextbox(content.d[2], content.d[1]);
    }

    if (isNew) {
        const action = {
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        };
        sendFunction(action);
    }
}

function removeTextbox(content, isNew) {
    let shape = layer.findOne(`#${getId(content)}`);
    if (shape) {
        shape.destroy();
    }

    if (isNew) {
        const action = {
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        };
        sendFunction(action);
    }
}

function remove(content, isNew) {
    let shape = layer.findOne(`#${getId(content)}`);
    if (!shape) {
        shape = lineLayer.findOne(`#${getId(content)}`);
    }
    if (!shape) {
        return;
    }
    shape.destroy();

    if (isNew) {
        const action = {
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        };
        sendFunction(action);
    }
}

function move(content, isNew) {
    let shape = layer.findOne(`#${getId(content)}`);
    if (!shape) {
        const shape = lineLayer.findOne(`#${getId(content)}`);
        if (shape) {
            if (
                [
                    "penSquigglyArrow",
                    "penSquigglyTab",
                    "penSquigglyFirstArrow",
                    "penSquigglyFirstTab",
                    "multiline",
                    "multilineDotted",
                    "multilineDashedArrow",
                    "multilineArrow",
                    "multilineHammer",
                    "bezierLine",
                    "bezierLineDashed",
                    "bezierLineTab",
                    "bezierLineTabDashed",
                ].includes(shape.attrs.instance.name) &&
                !content.v
            ) {
                const pos = shape.attrs.instance.getBoundingBox();
                shape.position({
                    x: pos.absX + content.d[1],
                    y: pos.absY + content.d[2],
                });
            } else {
                shape.position({ x: content.d[1], y: content.d[2] });
            }
        }
    } else {
        shape.position({ x: content.d[1], y: content.d[2] });
    }

    if (isNew) {
        const action = {
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        };
        sendFunction(action);
    }
}

function clear(isNew) {
    isConfirmClear.value = false;
    layer.destroyChildren();
    lineLayer.destroyChildren();
    state.drawBuffers = [];
    state.undoBuffers = [];

    state.lastDrawId = 0;
    state.activePlay = null;
    state.taggedVideosCount = 0;
    state.notes = "";
    if (isNew) {
        sendFunction({ t: "clear" });
        if (!props.isPlay) {
            axios.post("/meeting/clear-play", {
                roomId: props.data.room_id,
            });
        }
    }
    if (!props.isPlay) {
        emitter.emit("clear-wb");
    }
    layer.draw();
    lineLayer.draw();
}

function undo(isNew) {
    tr.nodes([]);
    layer.destroyChildren();
    lineLayer.destroyChildren();

    const undoStack = [];
    const draws = copy(state.drawBuffers);

    const lastDraw = draws[draws.length - 1];

    if (lastDraw?.b) {
        for (let i = draws.length - 1; i >= 0; i--) {
            if (draws[i].b) {
                undoStack.push(draws[i]);
                draws.pop();
            } else {
                break;
            }
        }
    } else {
        for (let i = draws.length - 1; i >= 0; i--) {
            if (draws[i].drawId === state.lastDrawId) {
                undoStack.push(draws[i]);
                draws.pop();
            } else {
                break;
            }
        }
    }

    state.undoBuffers = [...state.undoBuffers, ...undoStack];
    state.drawBuffers = [];

    for (let i = 0; i < draws.length; i++) {
        handleEvents(draws[i], false);
    }
    fixFlip();

    if (isNew) {
        sendFunction({ t: "undo" });
    }
}

function copy(obj) {
    return JSON.parse(JSON.stringify(obj));
}

function redo(isNew) {
    tr.nodes([]);
    const undoBuffers = copy(state.undoBuffers);
    if (undoBuffers.length === 0) return;

    const redoStack = [];
    const last = undoBuffers[undoBuffers.length - 1];
    if (last.b) {
        for (let i = undoBuffers.length - 1; i >= 0; i--) {
            if (undoBuffers[i].b) {
                redoStack.push(undoBuffers[i]);
                undoBuffers.pop();
            } else {
                break;
            }
        }
    } else {
        for (let i = undoBuffers.length - 1; i >= 0; i--) {
            if (undoBuffers[i].drawId === last.drawId) {
                redoStack.push(undoBuffers[i]);
                undoBuffers.pop();
            } else {
                break;
            }
        }
    }

    state.undoBuffers = undoBuffers;

    for (let i = 0; i < redoStack.length; i++) {
        handleEvents(
            {
                ...redoStack[i],
                drawId: ++state.lastDrawId,
            },
            true
        );
    }

    /* if (isNew) {
        sendFunction({ t: "redo" });
    } */
}

function clearCanvas(isNew) {
    lineLayer.destroyChildren();
    if (isNew) {
        const action = { t: "clearCanvas", drawId: ++state.lastDrawId };

        state.drawBuffers.push(action);
        sendFunction(action);
    }
}

function changeSize(e) {
    currentSize.value = parseInt(e.target.value);
    /* sendFunction({
        t: "setTextboxFontSize",
        d: [false, parseInt(e.target.value)],
        drawId: ++state.lastDrawId,
    }); */
}

function sendFunction(tool) {
    if (props.isPlay) {
        return;
    }
    signaling_socket.emit("drawToWhiteboard", {
        ...tool,
        wid: props.data.room_code,
        username: props.username,
    });
}

async function addWatermark() {
    const w = 300;
    const h = 80;

    const img = await loadImageAsset(
        "/default-images/whiteboard/watermark.jpg",
        682,
        173
    );

    const scale = stage.scale();

    const watermark = new Konva.Rect({
        x: 100,
        y: 100,
        width: w * scale.x,
        height: h * scale.y,
        opacity: 0.2,
        fillPatternImage: img,
        fillPatternScale: {
            x: (w * scale.x) / img.width,
            y: (h * scale.y) / img.height,
        },
        id: "watermark",
    });

    bgLayer.add(watermark);
}

function addWhiteBackground() {
    const scale = stage.scale(); // or layer.scale() if scaling the layer

    const bgCover = new Konva.Rect({
        x: 0,
        y: 0,
        width: stage.width() / scale.x,
        height: stage.height() / scale.x,
        fill: "white",
        name: "board",
    });
    bgLayer.add(bgCover);
}

async function addBackgroundImage(url) {
    const scale = stage.scale(); // or layer.scale() if scaling the layer
    const img = await loadImageAsset(url, 1920, 960);
    // Calculate scale to "contain" the image (fit entirely inside without cropping)
    const stageWidth = stage.width() / scale.x;
    const stageHeight = stage.height() / scale.y;
    const scaleX = stageWidth / img.width;
    const scaleY = stageHeight / img.height;
    const containScale = Math.min(scaleX, scaleY);

    const opacity = getBackgroundOpacity(url);
    // No offset needed for top-left
    bgImg = new Konva.Rect({
        x: 0,
        y: 0,
        width: img.width * containScale,
        height: img.height * containScale,
        fillPatternImage: img,
        fillPatternScale: {
            x: containScale,
            y: containScale,
        },
        name: "board",
        id: "bg",
        fillPatternRepeat: "no-repeat",
        fillPatternOffset: {
            x: 0,
            y: 0,
        },
        opacity,
    });
    bgLayer.add(bgImg);
}

async function changeBackgroundImage(content, isNew) {
    if (!content.d) {
        clearBackgroundImage(isNew);
        return;
    }
    if (!bgImg) {
        addBackgroundImage(content.d);
        return;
    }
    const img = await loadImageAsset(content.d, 1920, 960);
    const opacity = getBackgroundOpacity(content.d);
    state.bgOpacity = opacity * 100;
    bgImg.opacity(opacity);
    bgImg.fillPatternImage(img);
    bgImg.fill(null);
    if (isNew) {
        sendFunction({
            t: "changeBackground",
            d: content.d,
        });
    }
}

function clearBackgroundImage(isNew) {
    if (bgImg) {
        bgImg.fill("white");
        bgImg.fillPatternImage(null);
    }
    if (isNew) {
        sendFunction({
            t: "changeBackground",
        });
    }
}

function getBackgroundOpacity(imgUrl) {
    const path = "/default-images/backgrounds/";
    const imgOnly = imgUrl.split(path)[1].replace(".jpg", "");

    switch (imgOnly) {
        case "college-football-half-ez":
        case "college-football-half":
        case "football-college-goal":
        case "football-college-half-num":
        case "football-college-half":
        case "football-college":
        case "football-pro-goal":
        case "football-pro-half":
        case "football-pro":
        case "pro-football-half-ez":
        case "field-hockey-full":
        case "field-hockey-half":
        case "hockey-half":
        case "hockey":
        case "lacrosse-full":
        case "lacrosse-half":
        case "soccer-half":
        case "soccer":
            return 0.3;
        case "basketball-half":
        case "basketball":
        case "volley-ball-full":
        case "volley-ball-half":
            return 0.5;
    }

    return 0.3;
}

function createBezierPoints(points) {
    if (points.length >= 4) {
        return [
            points[0],
            points[1],
            points[points.length - 2],
            points[points.length - 1],
        ];
    }

    if (points.length == 2) {
        return [points[0], points[0], points[1], points[1]];
    }

    if (points.length == 3) {
        return [points[0], points[1], points[1], points[2]];
    }
}

async function loadImageAssets() {
    basketImage = await loadImageAsset(
        "/default-images/whiteboard/basket.webp",
        60,
        61
    );
    removerImage = await loadImageAsset(
        "/default-images/whiteboard/tool-remover.svg",
        512,
        512
    );
    removerImageBlue = await loadImageAsset(
        "/default-images/whiteboard/tool-remover-blue.svg",
        512,
        512
    );
}

let basketImage;
let removerImage;
let removerImageBlue;

function updateScale() {
    let proportionalWidth = window.innerWidth - 10;
    let maxHeight = window.innerHeight;

    if (
        props.relativeToContainer ||
        ["stacked-video", "stacked-play"].includes(preview.value.mode)
    ) {
        const container = document.getElementById("container");
        proportionalWidth = container.clientWidth;
        maxHeight = container.clientHeight;
    } else {
        const appSidebar = document.getElementById("app-sidebar");
        if (appSidebar) {
            proportionalWidth -= appSidebar.clientWidth;
        }
    }

    const meetWidget = document.getElementById("meet-widget");
    if (meetWidget) {
        maxHeight -= meetWidget.clientHeight;
    }

    let proportionalHeight = proportionalWidth * heightRatio;

    if (proportionalHeight > maxHeight) {
        proportionalHeight = maxHeight;
        proportionalWidth = proportionalHeight / heightRatio;
    }

    stage.width(proportionalWidth);
    stage.height(proportionalHeight);

    stage.scaleX(fixsx(proportionalWidth) / 1920);
    const width = stage.scaleX() < 0 ? stage.width() : 0;
    stage.x(width);
    stage.scaleY(proportionalWidth / 1920);
}

function getScaledPointerPosition(stage) {
    const pos = stage.getPointerPosition();
    const scale = stage.scale(); // or layer.scale() if scaling the layer
    return {
        x: pos.x / scale.x,
        y: pos.y / scale.y,
    };
}

function editBezierLine(data, isNew) {
    const shape = lineLayer.findOne(`#${getId(data)}`);
    if (shape.attrs.instance) {
        shape.attrs.instance.editBezier(data);
        lineLayer.draw();
    }

    if (isNew) {
        const content = data;
        const action = {
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        };
        sendFunction(action);
    }
}

function drawBezierLine(data, isNew) {
    const id = data.d[8] ? data.d[8] : "tx" + +new Date();
    const drawId = data.drawId != null ? data.drawId : ++state.lastDrawId;
    const shape = new CNLineFactory({
        ...data,
        id,
        removerImage: removerImageBlue,
        drawId,
    });
    lineLayer.add(shape.element);

    shape.setDragStart(function (evt) {
        hideAllDragger(false);
    });

    shape.setEditHandler(function (evt) {
        const action = {
            t: "editBezierLine",
            d: [...evt, shape.id],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });

    shape.setDragEnd(function (evt) {
        const pos = shape.element.position();
        const action = {
            t: "move",
            d: [shape.id, pos.x, pos.y],
            drawId: ++state.lastDrawId,
            v: 2,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });

    shape.setRemoverAction(function (evt) {
        shape.element.destroy();
        lineLayer.draw();
        const action = {
            t: "remove",
            d: [shape.id],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });

    shape.setDragClick(function (evt, color) {
        hideAllDragger(false);
        selectShape(shape);
    });

    if (isNew) {
        sendFunction({
            ...data,
            drawId,
        });
    }
}

function isDrawingTool(tool) {
    return [
        "pen",
        "penArrow",
        "penDotted",
        "penTab",
        "penDottedArrow",
        "penSquigglyArrow",
        "penSquigglyTab",
        "penSquigglyFirstArrow",
        "penSquigglyFirstTab",
        "multiline",
        "multilineDotted",
        "multilineDashedArrow",
        "multilineArrow",
        "multilineHammer",
        "bezierLine",
        "bezierLineDashed",
        "bezierLineTab",
        "bezierLineTabDashed",
    ].includes(tool);
}

function isMultilineTool(tool) {
    return [
        "penSquigglyArrow",
        "penSquigglyTab",
        "penSquigglyFirstArrow",
        "penSquigglyFirstTab",
        "multiline",
        "multilineDotted",
        "multilineDashedArrow",
        "multilineArrow",
        "multilineHammer",
        "bezierLine",
        "bezierLineDashed",
        "bezierLineTab",
        "bezierLineTabDashed",
    ].includes(tool);
}

function isFreeDrawingTool(tool) {
    return [
        "pen",
        "penArrow",
        "penDotted",
        "penTab",
        "penDottedArrow",
    ].includes(tool);
}

function isPlayerTool(tool) {
    return (
        [
            "circleFixed",
            "circleFilled",
            "Cross",
            "circleWithCross",
            "ShadedCross",
        ].includes(tool) ||
        tool.startsWith("offense:") ||
        tool.startsWith("defense:") ||
        tool.startsWith("letter:") ||
        tool.startsWith("icon:")
    );
}

function isDrawingToolDotted(tool) {
    return tool.indexOf("Dotted") !== -1 || tool.indexOf("Dashed") !== -1;
}

function isDrawingToolArrow(tool) {
    return (
        tool.indexOf("Arrow") !== -1 ||
        tool.indexOf("Tab") !== -1 ||
        tool.indexOf("Hammer") !== -1
    );
}

function cleanPlayJson(playJson) {
    if (typeof playJson === "object") {
        return playJson;
    }

    return JSON.parse(playJson);
}

async function getWhiteboard() {
    let jsonData = [];
    if (props.isPlay) {
        if (props.play) {
            jsonData = cleanPlayJson(props.play.play_json);
        }
    } else {
        const resp = await axios.get(
            `${props.whiteboardApi}api/loadwhiteboard?wid=${props.data.room_code}`
        );
        if (!resp.data) {
            return;
        }
        jsonData = resp.data;
    }
    // state.drawBuffers =
    // for (const tool of simplify(resp.data, props.sport)) {
    for (const tool of jsonData) {
        handleEvents(tool, false);
    }

    toggleRemover(false);
    toggleDragger(false);
    state.selectedShape = null;
    initYjs();
    currentTool.value = "mouse";
}

function drawLineAcross(tool, isNew) {
    const drawId = tool.drawId != null ? tool.drawId : ++state.lastDrawId;

    const line = new CNLineAcross({
        ...tool,
        drawId,
        removerImage: removerImageBlue,
        width: width,
    });

    lineLayer.add(line.element);
    line.element.moveToBottom();
    lineLayer.draw();

    currentTool.value = "";
    line.setRemoverAction(function () {
        line.element.destroy();
        lineLayer.draw();
        const action = {
            t: "remove",
            d: [line.id],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });
    line.setDragClick(function (evt) {
        hideAllDragger();

        selectShape(line);
    });
    line.setDragStart(function (evt) {
        hideAllDragger();
    });
    line.setDragEnd(function (evt) {
        const pos = line.element.position();
        const action = {
            t: "move",
            d: [line.id, 0, pos.y],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });

    line.setClickAction(function (color) {
        selectShape(line);
    });

    if (isNew) {
        sendFunction({
            ...tool,
            drawId,
        });
    }
}

function editMultiline(content, isNew) {
    const line = lineLayer.findOne(`#${getId(content)}`)?.attrs.instance;
    if (!line?.editPoints) return;
    line.editPoints(content.d[1]);
    if (isNew) sendFunction(content);
}

function drawMultiline(tool, isNew) {
    const drawId = tool.drawId != null ? tool.drawId : ++state.lastDrawId;

    const line = CNLineFactory({
        ...tool,
        d: tool.d.map((p) => ({ x: p.x, y: p.y })),
        drawId,
        removerImage: removerImageBlue,
    });

    lineLayer.add(line.element);
    line.dragger.visible(currentTool.value === "mouse");
    line.setEditHandler(function (points) {
        const action = {
            t: "editMultiline",
            d: [line.id, points],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });
    line.setRemoverAction(function () {
        line.element.destroy();
        lineLayer.draw();
        const action = {
            t: "remove",
            d: [line.id],
            drawId: ++state.lastDrawId,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });
    line.setDragStart(function (evt) {
        hideAllDragger();
    });
    line.setDragEnd(function (evt) {
        const pos = line.element.position();
        const action = {
            t: "move",
            d: [line.id, pos.x, pos.y],
            drawId: ++state.lastDrawId,
            v: 2,
        };
        state.drawBuffers.push(action);
        sendFunction(action);
    });
    line.setDragClick(function (evt) {
        hideAllDragger();

        selectShape(line);
    });

    line.setClickAction(function (color) {
        if (line.group) {
            selectGroup(line.group);
            return;
        }
        selectShape(line);
    });

    if (isNew) {
        sendFunction({
            ...tool,
            d: tool.d.map((p) => ({ x: p.x, y: p.y })),
            drawId,
        });
    }
}

function drawPen(tool, isNew) {
    const drawId = tool.drawId != null ? tool.drawId : ++state.lastDrawId;
    const id = tool.t + "" + drawId;

    const currentLine = lineLayer.findOne("#" + id);
    if (currentLine && currentLine.attrs.instance) {
        currentLine.attrs.instance.addPoints(tool.d);
        lineLayer.batchDraw();
    } else {
        const pen = CNPenFactory({
            ...tool,
            drawId,
            removerImage: removerImageBlue,
        });
        lineLayer.add(pen.element);
        lineLayer.draw();

        pen.setRemoverAction(function () {
            pen.element.destroy();
            lineLayer.draw();
            const action = {
                t: "remove",
                d: [pen.id],
                drawId: ++state.lastDrawId,
            };
            state.drawBuffers.push(action);
            sendFunction(action);
        });

        pen.setDragStart(function (evt) {
            hideAllDragger(false);
        });

        pen.setDragEnd(function (evt) {
            const pos = pen.element.position();
            const action = {
                t: "move",
                d: [pen.id, pos.x, pos.y],
                drawId: ++state.lastDrawId,
            };
            state.drawBuffers.push(action);
            sendFunction(action);
        });

        pen.setClickAction(function (color) {
            selectShape(pen);
        });

        pen.setDragClick(function (evt) {
            hideAllDragger(false);

            selectShape(pen);
        });
    }

    delete tool.visible;

    if (isNew) {
        sendFunction({
            ...tool,
            d: tool.d,
            drawId,
        });
    }
}

function changeOffense(content, isNew) {
    let letter = "";
    if (content.d.length >= 4) {
        letter = content.d[3].trim();
        letter = letter.toLowerCase();
    } else {
        letter = offenseTools(props.sport)[content.d[1]].trim();
        letter = letter.toLowerCase();
        content.d[3] = letter;
    }
    const textObj = layer.findOne(`#${getId(content)}-text`);
    textObj.text(CNTextbox.playerText(letter));

    textObj.offsetX(textObj.width() / 2);
    textObj.offsetY(textObj.height() / 2 - 1);
    if (isNew) {
        sendFunction({
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
            ...content,
        });
    }
}

function changeIcon(content, isNew) {
    const shape = layer.findOne(`#${getId(content)}`);
    if (shape.attrs.instance) {
        shape.attrs.instance.updateIcon(content.d[1]);
    }
    if (isNew) {
        sendFunction({
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
            ...content,
        });
    }
}

async function changePlay(content, isNew) {
    const resp = await axios.get("/plays/get-play-data", {
        params: { play_id: content.d[0] },
    });
    emitter.emit("video-tagged-wb", resp.data);
    state.activePlay = resp.data;
    state.notes = resp.data.play_note;
    if (isNew) {
        sendFunction(content);
    }
}

function handleVideoTagged(play) {
    emitter.emit("video-tagged-wb", play);
    sendFunction({
        t: "changePlay",
        d: [play.play_id],
    });
}

async function loadTaggedVideo(video) {
    const image = stage.toDataURL({
        pixelRatio: 2,
    });
    preview.value = {
        mode: "stacked-video",
        video,
        image,
    };
    rotateApp();
    updateResizeMode();

    ydoc?.transact(() => {
        ymeta?.set("mode", "stacked-video");
        ymeta?.set("video", video);
    });
    state.isLoadVideoModalOpen = false;
}

async function changeWhiteboardMode(content) {
    console.log("call");
    const image = stage.toDataURL({
        pixelRatio: 2,
    });
    const videoId = content.videoId;
    const video = content.video;

    preview.value.mode = content.mode;
    preview.value.videoId = videoId;
    preview.value.image = image;
    rotateApp();

    if (videoId) {
        const videoData = await axios("/video/" + videoId);
        preview.value.video = videoData.data;
    }

    if (video) {
        preview.value.video = video;
    }
}

emitter.on("change-preview-mode", function (mode) {
    preview.value.mode = mode;
    rotateApp();
    updateResizeMode();
    ydoc?.transact(() => {
        ymeta?.set("mode", mode);
    });
});

emitter.on("livestream-loaded", function ({ video }) {
    const image = stage.toDataURL({
        pixelRatio: 2,
    });

    preview.value = {
        mode: "stacked-video",
        video,
        image,
    };

    rotateApp();
    updateResizeMode();

    ydoc?.transact(() => {
        ymeta?.set("mode", "stacked-video");
        ymeta?.set("video", video);
    });
});

function updateResizeMode() {
    nextTick(() => {
        updateScale();
    });
}

function rotateApp() {
    if (!window.NativeApp) return;
    nextTick(() => {
        if (["stacked-video", "stacked-play"].includes(preview.value.mode)) {
            window.NativeApp.rotatePortrait();
        } else {
            window.NativeApp.rotateLandscape();
        }
    });
}

let ydoc = null;
let provider = null;
let ymeta = null;
function initYjs() {
    if (props.isPlay) return;
    ydoc = new Y.Doc();
    provider = new WebsocketProvider(
        props.websocketUrl,
        `war-room-${props.data.room_code}`,
        ydoc
    );

    ymeta = ydoc.getMap("preview");

    let lastPreview = null;
    let resizeTimer = null;
    function syncPreview() {
        const preview = ymeta.toJSON();
        const serialized = JSON.stringify(preview);
        console.log({ serialized, lastPreview });
        if (serialized === lastPreview) return;
        lastPreview = serialized;
        changeWhiteboardMode(ymeta.toJSON());

        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            updateResizeMode();
        }, 100);
    }

    ymeta.observe(() => {
        syncPreview();
    });

    provider.on("sync", (isSynced) => {
        if (isSynced) {
            syncPreview();
        }
    });
}

function handleSelectColor(color) {
    currentColor.value = color;
    const selected = state.selectedShape;
    if (
        !selected ||
        selected.isLocked ||
        typeof selected.setColor !== "function" ||
        !selected.element?.getLayer() ||
        (selected.getSelectedColor?.() ?? selected.color) === color
    ) {
        return;
    }

    const shadedIndex = selected.selectedCircleIndex;
    if (selected.name === "ShadedCross" && shadedIndex == null) return;

    handleEvents(
        {
            t: selected.name === "ShadedCross" ? "setShadedColor" : "setColor",
            d:
                selected.name === "ShadedCross"
                    ? [selected.id, shadedIndex, color]
                    : [selected.id, color],
            drawId: ++state.lastDrawId,
        },
        true
    );
}

function setColor(content, isNew) {
    let shape = layer.findOne(`#${content.d[0]}`);
    if (!shape) {
        shape = lineLayer.findOne(`#${content.d[0]}`);
    }

    if (shape) {
        shape.attrs?.instance?.setColor(content.d[1]);
        if (shape.attrs.instance) {
            shape.attrs.instance.color = content.d[1];
        }
        shape.getLayer()?.batchDraw();
    }

    if (isNew) {
        const action = {
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        };
        sendFunction(action);
    }
}

function setShadedColor(content, isNew) {
    let shape = layer.findOne(`#${content.d[0]}`);
    if (!shape) {
        return;
    }

    if (shape) {
        shape.attrs?.instance?.setShadedColor(content.d[1], content.d[2]);
        layer.draw();
    }

    if (isNew) {
        const action = {
            ...content,
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
        };
        sendFunction(action);
    }
}

function changeDefense(content, isNew) {
    let letter = "";
    if (content.d.length >= 4) {
        letter = content.d[3].toLowerCase().trim();
        letter = letter.toLowerCase();
    } else {
        letter = defenseTools(props.sport)[content.d[1]].trim();
        letter = letter.toLowerCase();
        content.d[3] = letter;
    }
    const textObj = layer.findOne(`#${getId(content)}-text`);
    textObj.text(CNTextbox.playerText(letter));

    textObj.offsetX(textObj.width() / 2);
    textObj.offsetY(textObj.height() / 2 - 1);
    if (isNew) {
        sendFunction({
            drawId:
                content.drawId != null ? content.drawId : ++state.lastDrawId,
            ...content,
        });
    }
}

function drawBasketPlayer() {
    const drawId = ++state.lastDrawId;
    const flatten = basketPlayer;
    // const image = "/default-images/backgrounds/basketball-half.jpg";
    // changeBackgroundImage(image, true);
    // window.postMessage({ action: "load_background", image });
    for (let i = 0; i < flatten.length; i++) {
        flatten[i].drawId = drawId;
        handleEvents(flatten[i], true);
    }
}

const imageFileRef = ref(null);
function handleEmbedImage() {
    imageFileRef.value.click();
}

const handleEmbedImageChange = (e) => {
    const files = imageFileRef.value.files;
    const file = files[0];
    if (file) {
        const reader = new FileReader();
        reader.onloadend = (e) => {
            const imageObj = new Image();
            imageObj.onload = function () {
                const imWidth = imageObj.naturalWidth;
                const imHeight = imageObj.naturalHeight;
                const aspectRatio = imWidth / imHeight;
                const width = 100;
                const height = width / aspectRatio;
                const image = new Konva.Image({
                    x: 50,
                    y: 50,
                    image: imageObj,
                    width,
                    height,
                });

                image.draggable(true);
                layer.add(image);
            };
            imageObj.src = reader.result;
        };
        reader.readAsDataURL(file);
    }
};

async function downloadImage() {
    const prevTool = currentTool.value;
    currentTool.value = "";
    const dataURL = stage.toDataURL({
        pixelRatio: 2, // double resolution
    });
    toggleDragger(false);
    toggleRemover(false);

    const pdf = new jsPDF({ orientation: "landscape" });

    // Get page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Calculate image dimensions
    const ratio = state.notes ? 2 : 3;
    const imageAreaWidth = (ratio / 3) * pageWidth;
    const imageAspectRatio = 1920 / 960; // 2:1
    const imageHeight = imageAreaWidth / imageAspectRatio;
    const xOffset = (pageWidth - imageAreaWidth) / 2;

    pdf.addImage(
        dataURL,
        "JPEG",
        xOffset,
        5,
        imageAreaWidth,
        imageHeight,
        undefined,
        "SLOW"
    );

    if (state.notes) {
        const y = imageHeight + 8;
        const page = document.createElement("div"); // append stuff into the div
        page.style.fontSize = "12px";
        page.innerHTML = state.notes;
        page.style.width = imageAreaWidth + xOffset + "mm";
        page.style.display = "block";

        await pdf.html(page, {
            y,
            x: xOffset,
            width: pageWidth,
            html2canvas: {
                useCORS: true,
                allowTaint: true,
                letterRendering: true,
                logging: false,
                scale: 0.2,
            },
        });
    }

    pdf.save(`${props.title}.pdf`);

    setTimeout(function () {
        currentTool.value = prevTool;
    }, 200);
}

function flip() {
    state.isFlipped = !state.isFlipped;
    stage.scaleX(fixsx(stage.scaleX()));
    const width = stage.scaleX() < 0 ? stage.width() : 0;
    stage.x(width);

    fixFlip();
    stage.draw();
}

function fixFlip() {
    const texts = layer.find("Text, .text");
    texts.forEach((text) => {
        text.scaleX(fixsx(text.scaleX()));
    });

    const watermark = bgLayer.findOne("#watermark");
    const watermarkNewScale = fixsx(watermark.scaleX());
    watermark?.scaleX(watermarkNewScale);
    if (watermarkNewScale < 0) {
        watermark.x(1820);
    } else {
        watermark.x(100);
    }

    /* const bg = bgLayer.findOne("#bg");
    if (bg) {
        const bgNewScale = fixsx(bg.scaleX());
        bg?.scaleX(bgNewScale);
        if (bgNewScale < 0) {
            bg.x(1920);
        } else {
            bg.x(0);
        }
    } */
}

function fixsx(sx) {
    const v = state.isFlipped ? -1 : 1;
    return Math.abs(sx) * v;
}

const playName = computed(() => {
    if (props.isPlay && props.play?.is_template) {
        return `${props.play?.play_name} (Template)`;
    }
    if (props.isPlay) {
        return props.play?.play_name;
    }

    if (!props.isGuest) {
        return state.activePlay?.play_name || "Untitled";
    }
});

function toggleEraser() {
    if (currentTool.value === "eraser") {
        currentTool.value = "";
    } else {
        currentTool.value = "eraser";
    }
}

const isPracticePlanOpen = ref(false);
const isGamePlanOpen = ref(false);

const practicePlanData = ref(null);
const gamePlanData = ref(null);

emitter.on("whiteboard-toggle-action", function (action) {
    switch (action) {
        case "togglePracticePlan":
            togglePracticePlan();
            break;
        case "toggleGamePlan":
            toggleGamePlan();
            break;
    }
});

const togglePracticePlan = async () => {
    isPracticePlanOpen.value = true;
    if (!practicePlanData.value) {
        try {
            const res = await axios.get("/practice_plans", {
                params: { teamId: props.teamId },
                headers: { Accept: "application/json" },
            });
            practicePlanData.value = res.data;
        } catch (error) {
            console.error("Failed to fetch Practice Plans", error);
        }
    }
};

const toggleGamePlan = async () => {
    isGamePlanOpen.value = true;
    if (!gamePlanData.value) {
        try {
            const res = await axios.get("/game_plans", {
                params: { teamId: props.teamId },
                headers: { Accept: "application/json" },
            });
            gamePlanData.value = res.data;
        } catch (error) {
            console.error("Failed to fetch Game Plans", error);
        }
    }
};

const toggleAnimation = () => {
    if (!props.isPlay) return;
    animation.value.active = true;
    animation.value.camera3d = false;
    animation.value.showLines = true;
    lineLayerListeningBeforeAnimation = lineLayer.listening();
    lineLayerOpacityBeforeAnimation = lineLayer.opacity();
    animLayer.show();
    layer.hide();
    lineLayer.show();
    lineLayer.listening(false);
    lineLayer.opacity(0.2);
    trLayer.hide();

    // anim
    CNAnim = new Animation(stage, animLayer, {
        playerSpeeds: animation.value.playerSpeeds,
        selectedPlayerId: animation.value.selectedPlayerId,
        onPlayerSelect: ({ id, label, speed }) => {
            animation.value.selectedPlayerId = id;
            animation.value.selectedPlayerLabel = label;
            animation.value.selectedPlayerSpeed = speed;
        },
    });
    CNAnim.reinit(getPlayers(), animation.value.frames, getAnimationPaths());
    syncAnimationState();
};

function syncAnimationState() {
    if (!CNAnim) return;
    animation.value.frames = { ...CNAnim.frames };
    animation.value.currentFrame = CNAnim.currentFrame;
    animation.value.playing = CNAnim.isPlaying;
    animation.value.playerSpeeds = { ...CNAnim.playerSpeeds };
    animation.value.selectedPlayerId = CNAnim.selectedPlayerId;
    animation.value.selectedPlayerLabel = CNAnim.selectedPlayerLabel;
    animation.value.hasBall = CNAnim.hasBall();
    animation.value.selectedPlayerSpeed = CNAnim.getPlayerSpeed(
        CNAnim.selectedPlayerId
    );
}

function getPersistedAnimation() {
    if (!props.isPlay) return null;
    if (CNAnim) {
        CNAnim.captureCurrentFrame();
        syncAnimationState();
    }
    return {
        version: 1,
        frames: JSON.parse(JSON.stringify(animation.value.frames || {})),
        playerSpeeds: JSON.parse(
            JSON.stringify(animation.value.playerSpeeds || {})
        ),
    };
}

function toggleThreeCameraView() {
    if (animation.value.camera3d) {
        disableThreeCameraView();
        return;
    }

    try {
        threeCameraView = new ThreeCameraView(stage, {
            preset: animation.value.cameraPreset,
            playerLayer: animLayer,
            getPlayers: () => CNAnim?.players,
        });
        threeCameraView.mount();
        animation.value.camera3d = true;
    } catch (error) {
        threeCameraView?.destroy();
        threeCameraView = null;
        animation.value.camera3d = false;
        console.error("Unable to start the 3D camera view", error);
    }
}

function disableThreeCameraView() {
    threeCameraView?.destroy();
    threeCameraView = null;
    animation.value.camera3d = false;
}

function animationAction(action, value) {
    if (!CNAnim) return;

    switch (action) {
        case "animationPrevious":
            CNAnim.captureCurrentFrame();
            CNAnim.previousFrame();
            break;
        case "animationNext":
            CNAnim.captureCurrentFrame();
            CNAnim.nextFrame();
            break;
        case "animationAddFrame":
            CNAnim.addFrame();
            break;
        case "animationDeleteFrame":
            CNAnim.deleteFrame();
            break;
        case "animationTogglePlayback":
            if (CNAnim.isPlaying) {
                CNAnim.stop();
            } else {
                CNAnim.play({
                    onFrame: syncAnimationState,
                    onStateChange: syncAnimationState,
                });
            }
            break;
        case "animationToggleLines":
            animation.value.showLines = !animation.value.showLines;
            lineLayer.visible(animation.value.showLines);
            lineLayer.batchDraw();
            break;
        case "animationSetPlayerSpeed":
            CNAnim.setPlayerSpeed(value);
            break;
        case "animationAddBall":
            CNAnim.addBall();
            break;
        case "animationToggle3D":
            toggleThreeCameraView();
            break;
        case "animationCameraPreset":
            animation.value.cameraPreset = value;
            threeCameraView?.setPreset(value);
            break;
        case "animationClose":
            CNAnim.captureCurrentFrame();
            CNAnim.stop();
            animation.value.active = false;
            break;
    }

    syncAnimationState();
}

watch(
    () => animation.value.active,
    (val) => {
        if (!val) {
            disableThreeCameraView();
            CNAnim?.stop();
            animation.value.playing = false;
            layer.show();
            lineLayer.show();
            lineLayer.listening(lineLayerListeningBeforeAnimation);
            lineLayer.opacity(lineLayerOpacityBeforeAnimation);
            trLayer.show();
            animLayer.hide();
        }
    }
);

function toolbarAction(action, value) {
    switch (action) {
        case "undo":
            undo(true);
            break;
        case "redo":
            redo(true);
            break;
        case "ai":
            isAiOpen.value = !isAiOpen.value;
            break;
        case "clear":
            clear(true);
            break;
        case "changeSize":
            changeSize();
            break;
        case "drawBasketPlayer":
            drawBasketPlayer();
            break;
        case "lockWhiteboard":
            lockWhiteboard();
            break;
        case "unlockWhiteboard":
            unlockWhiteboard();
            break;
        case "lock":
            lockShape();
            break;
        case "unlock":
            unlockShape();
            break;
        case "group":
            toggleGroup();
            break;
        case "ungroup":
            toggleUngroup();
            break;
        case "lockGroup":
            toggleGroupLock();
            break;
        case "unlockGroup":
            toggleGroupUnlock();
            break;
        case "flip":
            flip();
            break;
        case "drawText":
            drawText();
            break;
        case "download":
            downloadImage();
            break;
        case "copy":
            handleCopy();
            break;
        case "paste":
            handlePaste();
            break;
        case "togglePracticePlan":
            togglePracticePlan();
            break;
        case "toggleGamePlan":
            toggleGamePlan();
            break;
        case "toggleAnimation":
            toggleAnimation();
            break;
        case "animationPrevious":
        case "animationNext":
        case "animationAddFrame":
        case "animationDeleteFrame":
        case "animationTogglePlayback":
        case "animationToggleLines":
        case "animationSetPlayerSpeed":
        case "animationAddBall":
        case "animationToggle3D":
        case "animationCameraPreset":
        case "animationClose":
            animationAction(action, value);
            break;
    }
}

function lockShape() {
    if (!state.selectedShape) {
        return;
    }
    state.selectedShape.isLocked = true;
    state.selectedShape.element.draggable(false);
    isLocked.value = true;
}

function unlockShape() {
    if (!state.selectedShape) {
        return;
    }

    state.selectedShape.element.draggable(true);
    state.selectedShape.isLocked = false;
    isLocked.value = false;
}

function lockWhiteboard() {
    currentTool.value = "";
    layer.listening(false);
    lineLayer.listening(false);
    isLocked.value = true;
}

function unlockWhiteboard() {
    layer.listening(true);
    lineLayer.listening(true);
    isLocked.value = false;
}

function pasteShape() {
    if (state.clipboards.length == 0) return;
    if (state.clipboards[0] === state.lastCopiedShape) {
        state.lastCopyCount++;
    } else {
        state.lastCopyCount = 1;
    }
    state.lastCopiedShape = state.clipboards[0];

    const tool = copy(state.clipboards[0].data);

    if (state.clipboards[0].pasteData) {
        tool.d = state.clipboards[0].pasteData(state.lastCopyCount);
    }
    handleEvents(
        {
            ...tool,
            drawId: ++state.lastDrawId,
        },
        true
    );
}

async function pasteShapeGroup() {
    if (state.clipboards.length < 1) return;
    const drawId = ++state.lastDrawId;

    let newShapeIds = [];
    for (let i = 0; i < state.clipboards.length; i++) {
        const tool = copy(state.clipboards[i].data);
        console.log("idbefore", getId(tool));

        if (state.clipboards[i].pasteData) {
            tool.d = state.clipboards[i].pasteData(0);
            console.log("idnext", getId(tool));
            newShapeIds.push(getId(tool));
        }
        handleEvents(
            {
                ...tool,
                drawId,
            },
            true
        );
        await sleep(10);
    }

    if (state.clipboards[0].group) {
        let shapes = [];
        for (let i = 0; i < newShapeIds.length; i++) {
            const shape = getShapeById(newShapeIds[i]);
            shapes.push(shape);
        }

        state.selectedShapes = [...shapes];
        state.isGroupSelected = true;
        tr.nodes(shapes);
        toggleGroup();
    }

    trLayer.batchDraw();
    layer.batchDraw();
}

function getShapeById(id) {
    let shape = layer.findOne(`#${id}`);
    if (!shape) {
        shape = lineLayer.findOne(`#${id}`);
    }

    if (shape) {
        return shape;
    }
}

function handleCopy() {
    if (state.selectedShapes.length > 0) {
        state.clipboards = state.selectedShapes.map((s) => s.attrs.instance);
    } else if (state.selectedShape) {
        state.clipboards = [state.selectedShape];
    }
}

function handlePaste() {
    if (state.clipboards.length > 1) {
        pasteShapeGroup();
    } else if (state.clipboards.length === 1) {
        pasteShape();
    }
}

function handleKeyboardEvent(e) {
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

    const copyKey = isMac ? e.metaKey : e.ctrlKey;

    if (copyKey && e.code === "KeyC") {
        handleCopy();
    } else if (e.code === "KeyV") {
        handlePaste();
    } else if (e.code === "Escape") {
        state.clipboards = [];
    } else if (e.code === "KeyZ") {
        undo(true);
    } else if (e.code === "KeyY") {
        redo(true);
    }
}

function clearDefenses(isNew) {
    const children = layer.getChildren((node) => {
        return node?.attrs?.instance?.position === "defense";
    });
    children.forEach((node) => {
        node.remove();
    });
    if (isNew) {
        sendFunction({
            drawId: ++state.lastDrawId,
            t: "clearDefenses",
        });
    }
}
function clearOffenses(isNew) {
    const children = layer.getChildren((node) => {
        return node?.attrs?.instance?.position === "offense";
    });
    children.forEach((node) => {
        node.remove();
    });
    if (isNew) {
        sendFunction({
            drawId: ++state.lastDrawId,
            t: "clearOffenses",
        });
    }
}

function getPlayers() {
    const children = layer.getChildren((node) => {
        return Boolean(node?.attrs?.instance?.position);
    });
    return children;
}

function getAnimationPaths() {
    return lineLayer.getChildren((node) => {
        const name = node?.attrs?.instance?.name || "";
        return (
            name.startsWith("multiline") ||
            [
                "penSquigglyArrow",
                "penSquigglyTab",
                "penSquigglyFirstTab",
                "penSquigglyFirstArrow",
                "bezierLine",
                "bezierLineDashed",
                "bezierLineTab",
                "bezierLineTabDashed",
            ].includes(name)
        );
    });
}
</script>

<template>
    <WhiteboardToolbar
        :preview-mode="preview.mode"
        :play-name="playName"
        :sport="props.sport"
        :is-guest="props.isGuest"
        :view-only="props.viewOnly"
        :is-free="props.isFree"
        :is-play="props.isPlay"
        :is-multi-select="state.selectedShapes.length > 0"
        :is-group="state.isGroupSelected"
        :is-group-locked="state.isGroupLocked"
        :is-copy-enabled="
            state.selectedShapes.length > 0 || state.selectedShape
        "
        :is-paste-enabled="state.clipboards.length > 0"
        v-show="
            preview.mode != 'video-fullscreen' &&
            preview.mode != 'floating-play'
        "
        @action="toolbarAction"
    >
        <template v-slot:misc>
            <button @click="handleEmbedImage()">
                <input
                    ref="imageFileRef"
                    type="file"
                    style="display: none"
                    id="imageFile"
                    accept="image/jpeg,image/png"
                    @change="handleEmbedImageChange"
                />
                <img src="/default-images/whiteboard/tool-embed-image.svg" />
            </button>
            <button @click="state.isLoadVideoModalOpen = true" class="relative">
                <img src="/default-images/whiteboard/tool-video.svg" />
                <span
                    v-show="state.activePlay"
                    class="
                        absolute
                        font-bold
                        top-[-13px]
                        right-[-1px]
                        text-[#0FA3F0]
                        bg-[#e2f3fb]
                        border-2 border-solid border-[#0FA3F0]
                        rounded-full
                        text-[11px]
                        padding-[2px]
                        w-[18px]
                        h-[18px]
                    "
                    >{{ state.activePlay?.videos?.length || 0 }}</span
                >
            </button>

            <button
                @dblclick="clearCanvas"
                @click="toggleEraser()"
                :class="{ active: currentTool == 'eraser' }"
            >
                <img src="/default-images/whiteboard/tool-eraser.svg" />
            </button>
        </template>
    </WhiteboardToolbar>
    <div
        v-show="state.isToolbarCollapsed"
        class="toolbar absolute z-[100] items-center bg-white hidden"
    >
        <button @click="state.isToolbarCollapsed = false" class="m-2">
            <img
                src="/default-images/whiteboard/tool-collapse.svg"
                class="rotate-180"
                width="15"
            />
        </button>
    </div>

    <div
        class="flex flex-col-reverse"
        :class="{
            '!flex-col': preview.mode == 'stacked-play',
        }"
    >
        <div
            class="whiteboard"
            id="container"
            :class="{
                '!h-[50vh]': ['stacked-video', 'stacked-play'].includes(
                    preview.mode
                ),
                'justify-items-center': [
                    'stacked-video',
                    'stacked-play',
                ].includes(preview.mode),
                'bg-white': ['stacked-video', 'stacked-play'].includes(
                    preview.mode
                ),
            }"
            v-show="
                preview.mode != 'video-fullscreen' &&
                preview.mode != 'floating-play'
            "
        ></div>
        <VideoPreview></VideoPreview>
    </div>

    <!-- AI Drawer -->
    <whiteboard-ai-drawer
        v-if="isAiOpen"
        :play="state.activePlay || props.play"
        :room-id="props.isPlay ? null : props.data?.room_id || null"
        :team="state.team || props.team"
        :draw-buffers="state.drawBuffers"
        @close="isAiOpen = false"
        @actions="handleAiActions"
    ></whiteboard-ai-drawer>

    <play-notes
        :is-open="isNoteOpen"
        :is-war-room="!!props.data?.room_id"
        :play="state.activePlay"
        :meeting-code="props.data?.room_code"
        v-model="state.notes"
        @update:modelValue="updateNotes"
        @close="isNoteOpen = false"
    ></play-notes>
    <load-tagged-video-modal
        :is-open="state.isLoadVideoModalOpen"
        :whiteboard-api="whiteboardApi"
        :play="state.activePlay"
        @loaded="loadTaggedVideo"
        @tagged="handleVideoTagged"
        @closed="state.isLoadVideoModalOpen = false"
        class="py-2 px-3 my-1 hover:bg-gray-100 cursor-pointer"
    >
        Load Video...
    </load-tagged-video-modal>
    <div class="hidden">
        <input v-model="state.mdata.x" />
        <input v-model="state.mdata.y" />
        <input v-model="state.mresult" />
    </div>
    <div class="hidden">
        Background Opacity:
        <input v-model="state.bgOpacity" type="number" min="0" max="100" />
    </div>
    <button class="hidden" @click="getPlayers">get</button>
    <!-- Modals -->
    <ModalCustom
        :is-open="isPracticePlanOpen"
        @close="isPracticePlanOpen = false"
        :z-index="1002"
    >
        <button
            type="button"
            class="
                absolute
                top-[-12px]
                right-[-12px]
                z-[1002]
                rounded-full
                leading-[0]
                p-2
                hover:bg-red-400
                bg-red-500
                text-white
                hover:text-white
                transition-colors
            "
            @click="isPracticePlanOpen = false"
        >
            <ion-icon name="close" class="text-xl"></ion-icon>
        </button>
        <div class="max-h-[90vh] overflow-y-auto">
            <PracticePlanContainer
                v-if="practicePlanData"
                :websocket-url="props.websocketUrl"
                :env="props.env"
                :team="practicePlanData.team"
                :available-tags="practicePlanData.availableTags"
                :initial-plans="practicePlanData.plans"
                :has-access="practicePlanData.hasAccess"
                :can-manage-access="practicePlanData.canManageAccess"
                :current-access-tags="practicePlanData.currentAccessTags"
                :current-user-name="practicePlanData.currentUserName"
            />
            <div v-else class="p-8 text-center">Loading...</div>
        </div>
    </ModalCustom>

    <ModalCustom
        :is-open="isGamePlanOpen"
        @close="isGamePlanOpen = false"
        :z-index="1002"
    >
        <button
            type="button"
            class="
                absolute
                top-[-12px]
                right-[-12px]
                z-[1002]
                rounded-full
                leading-[0]
                p-2
                hover:bg-red-400
                bg-red-500
                text-white
                hover:text-white
                transition-colors
            "
            @click="isGamePlanOpen = false"
        >
            <ion-icon name="close" class="text-xl"></ion-icon>
        </button>
        <div class="max-h-[90vh] overflow-y-auto relative">
            <GamePlanContainer
                v-if="gamePlanData"
                :websocket-url="props.websocketUrl"
                :env="props.env"
                :team="gamePlanData.team"
                :available-tags="gamePlanData.availableTags"
                :initial-plans="gamePlanData.plans"
                :has-access="gamePlanData.hasAccess"
                :can-manage-access="gamePlanData.canManageAccess"
                :current-access-tags="gamePlanData.currentAccessTags"
                :current-user-name="gamePlanData.currentUserName"
            />
            <div v-else class="p-8 text-center">Loading...</div>
        </div>
    </ModalCustom>
</template>

<style>
#container {
    position: relative;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    box-sizing: border-box;
}
.whiteboard-three-canvas {
    cursor: grab;
}
.whiteboard-three-canvas:active {
    cursor: grabbing;
}
canvas:focus-visible,
canvas:focus,
.whiteboard:focus,
.whiteboard:focus-visible {
    outline: none !important;
}
</style>
